<template>
  <header class="dk-header" :class="headerClass">
    <div class="dk-container dk-header__row">
      <button type="button" class="dk-burger" aria-label="Open menu" @click="mobileOpen = true">
        <span></span><span></span><span></span>
      </button>

      <!-- `hdrLogo` IS the `logo` block's settings, so the block's own layout
           overrides land here — the header section has exactly one block. -->
      <RouterLink
        to="/"
        class="dk-logo"
        :style="[logoVars, blockLayoutStyle(hdrLogo)]"
        :class="blockLayoutClass(hdrLogo)"
        :data-dk-block="hdrLogoId"
      >
        <img
          v-if="logoSrc"
          :src="logoSrc"
          :alt="logo?.alt || bootstrap.storeName"
          class="dk-logo__img"
          style="height: auto; display: block"
        />
        <!-- The inverted logo only exists while the header overlays a hero, and
             is swapped by CSS so there is no flash on route change. -->
        <img
          v-if="logoInverted"
          :src="logoInverted"
          :alt="logo?.alt || bootstrap.storeName"
          class="dk-logo__img dk-logo__img--inv"
          style="height: auto; display: block"
        />
        <template v-if="!logoSrc && !logoInverted">{{ bootstrap.storeName || "Store" }}</template>
      </RouterLink>

      <nav class="dk-nav" @mouseleave="openMenu = -1">
        <div
          v-for="(item, i) in menu"
          :key="item.url || item.label"
          class="dk-nav__item"
          :class="{ 'dk-nav__item--open': openMenu === i }"
          @mouseenter="hasPanel(item) && (openMenu = i)"
        >
          <RouterLink
            v-if="item.url"
            :to="item.url"
            class="dk-nav__link"
            @focus="hasPanel(item) && (openMenu = i)"
          >{{ item.label }}<span v-if="hasPanel(item)" class="dk-nav__caret" aria-hidden="true">▾</span></RouterLink>
          <button
            v-else
            type="button"
            class="dk-nav__link"
            :aria-expanded="openMenu === i"
            @click="openMenu = openMenu === i ? -1 : i"
          >{{ item.label }}<span class="dk-nav__caret" aria-hidden="true">▾</span></button>

          <div
            v-if="hasPanel(item) && openMenu === i"
            class="dk-mega"
            :class="{ 'dk-mega--feature': item.image, 'dk-mega--simple': isSimple(item) }"
          >
            <div class="dk-mega__cols">
              <div v-for="child in item.children" :key="child.url || child.label" class="dk-mega__col">
                <template v-if="child.children && child.children.length">
                  <RouterLink v-if="child.url" :to="child.url" class="dk-mega__h">{{ child.label }}</RouterLink>
                  <span v-else class="dk-mega__h">{{ child.label }}</span>
                  <RouterLink v-for="gc in child.children" :key="gc.url" :to="gc.url" class="dk-mega__link">{{ gc.label }}</RouterLink>
                </template>
                <RouterLink v-else-if="child.url" :to="child.url" class="dk-mega__link dk-mega__link--solo">{{ child.label }}</RouterLink>
              </div>
            </div>
            <RouterLink v-if="item.image" :to="item.image_url || item.url || '/'" class="dk-mega__feature">
              <img :src="item.image" :alt="item.image_label || item.label" loading="lazy" />
              <span class="dk-mega__feature-cap">{{ item.image_label || item.label }}</span>
            </RouterLink>
          </div>
        </div>
      </nav>

      <div class="dk-header__actions">
        <!-- Mobile: a search icon toggles a full-width search bar. Desktop: the
             field is inline, and the toggle is hidden by CSS — unless the theme's
             `search_style` is "icon", where desktop behaves like mobile. -->
        <button
          v-if="showSearch"
          type="button"
          class="dk-iconbtn dk-search-toggle"
          :aria-label="searchOpen ? 'Close search' : 'Search'"
          :aria-expanded="searchOpen"
          @click="toggleSearch"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
          </svg>
        </button>

        <!-- Predictive search: results appear as you type. -->
        <div v-if="showSearch" class="dk-search-wrap" :class="{ 'dk-search-wrap--open': searchOpen }" @focusout="onFocusOut">
          <form class="dk-search" @submit.prevent="submitSearch">
            <input
              ref="searchInput"
              v-model="q"
              type="search"
              placeholder="Search…"
              aria-label="Search products"
              autocomplete="off"
              @focus="q.trim() && (showResults = true)"
            />
          </form>

          <!-- Only ever visible in the expanded desktop bar: on mobile the header's
               own icon stays on screen and is already the way back out. -->
          <button
            v-if="searchOpen"
            type="button"
            class="dk-search-close"
            aria-label="Close search"
            @click="closeSearch"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>

          <div v-if="showResults && q.trim().length >= 2" class="dk-predict">
            <p v-if="searching && !results.length" class="dk-predict__note dk-muted">Searching…</p>
            <p v-else-if="!results.length" data-dk-mask-text class="dk-predict__note dk-muted">No matches for “{{ q.trim() }}”.</p>
            <template v-else>
              <RouterLink
                v-for="p in results"
                :key="p.handle"
                :to="`/products/${p.handle}`"
                class="dk-predict__item"
                @click="closeResults"
              >
                <img v-if="p.image_src" :src="sizedImage(p.image_src, 96)" :alt="p.title" loading="lazy" />
                <span class="dk-predict__t">{{ p.title }}</span>
                <span v-if="displayCardPrice(p) != null" class="dk-predict__p">{{ money(displayCardPrice(p)) }}</span>
              </RouterLink>
              <button type="button" data-dk-mask-text class="dk-predict__all" @click="submitSearch">
                See all results for “{{ q.trim() }}”
              </button>
            </template>
          </div>
        </div>

        <RouterLink :to="account.isAuthed ? '/account' : '/account/login'" class="dk-actionlink dk-actionlink--account" aria-label="Account">
          <svg class="dk-actionlink__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.2 4-6.5 8-6.5s8 2.3 8 6.5" />
          </svg>
          <span class="dk-actionlink__text">{{ account.isAuthed ? "Account" : "Sign in" }}</span>
        </RouterLink>
        <button type="button" class="dk-actionlink dk-cartbtn" aria-label="Open cart" @click="cart.openCart()">
          <svg class="dk-actionlink__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 8h12l1 12H5z" /><path d="M9 8a3 3 0 0 1 6 0" />
          </svg>
          <span class="dk-actionlink__text">Cart</span>
          <span v-if="cart.count" class="dk-cartlink__count">{{ cart.count }}</span>
        </button>
      </div>
    </div>

    <MobileNav v-model:open="mobileOpen" :items="menu" />
  </header>
</template>

<script setup lang="ts">
import { blockLayoutClass, blockLayoutStyle } from "@dk/theme"
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { api, type ProductCard } from "../lib/api"
import { logoWidthVars } from "../lib/headerLogo"
import { sizedImage } from "../lib/image"
import { displayCardPrice } from "../lib/displayPrice"
import { money } from "../lib/money"
import { useAccountStore } from "../stores/account"
import { useBootstrapStore, type MenuItem } from "../stores/bootstrap"
import { useCartStore } from "../stores/cart"
import { imgSrc } from "../sections/helpers"
import MobileNav from "./MobileNav.vue"

const bootstrap = useBootstrapStore()
const cart = useCartStore()
const account = useAccountStore()
const router = useRouter()
const route = useRoute()
const q = ref("")
const mobileOpen = ref(false)
// The search field is collapsed behind an icon and expands into a full-width bar.
// Always so on mobile; on desktop only when the theme picks `search_style: "icon"`
// (otherwise the field is inline and the toggle is CSS-hidden). One piece of state
// either way — which style applies is a pure CSS decision, so nothing here has to
// know the viewport width.
const searchOpen = ref(false)
const searchInput = ref<HTMLInputElement>()
function toggleSearch() {
  searchOpen.value = !searchOpen.value
  if (searchOpen.value) nextTick(() => searchInput.value?.focus())
}
function closeSearch() {
  closeResults()
  searchOpen.value = false
}

// ---- the `header` layout section -------------------------------------------
// Its settings existed in the schema from the start but NOTHING read them, so
// three shipped themes have been storing `transparent_home: true` inertly. The
// resolve order is: the section's value when set, else today's source — a store
// with no header section, or an empty one, renders byte-identically.
const hdr = computed<Record<string, unknown>>(() => {
  const list = bootstrap.themeDoc?.layout?.header ?? []
  return (list.find((x) => x.type === "header")?.settings ?? {}) as Record<string, unknown>
})
const hdrLogoBlock = computed(() =>
  (bootstrap.themeDoc?.layout?.header ?? [])
    .find((x) => x.type === "header")
    ?.blocks?.find((x) => x.type === "logo"),
)
const hdrLogo = computed(() => (hdrLogoBlock.value?.settings ?? {}) as Record<string, unknown>)
const hdrLogoId = computed(() => hdrLogoBlock.value?.id)

const logo = computed(() => bootstrap.themeSettings.logo)
const logoSrc = computed(() => imgSrc(hdrLogo.value.logo) || imgSrc(hdr.value.logo) || logo.value?.src || "")
const logoInverted = computed(() => imgSrc(hdrLogo.value.logo_inverted))
// An unset mobile width falls back to the desktop one THERE, not in the
// stylesheet: `var(--dk-logo-w-m, …)` could never take its fallback, because the
// property was always emitted — as `0px` when no mobile width was set. So
// `.dk-logo__img` resolved to `max-width: 0` and an image logo was INVISIBLE on
// every phone, which is the state most stores are in (the field is optional, and
// nothing in the builder hints it is required). A store with no logo was
// unaffected, which is why it went unnoticed: the store-name fallback is text,
// and the cap only ever applied to the image.
const logoVars = computed(() => logoWidthVars(hdrLogo.value, logo.value?.width))

const showSearch = computed(() => hdr.value.show_search !== false)
/** Desktop search presentation. Anything other than the explicit "icon" is the
 *  inline bar, so a store saved before this setting existed — and every one of
 *  the 14 templates, none of which emit the key — keeps today's header. */
const searchIcon = computed(() => hdr.value.search_style === "icon")
/** Impulse's `sticky_index`: a transparent header laid over the home hero.
 *  Stored under the id three templates already use, so nothing migrates. */
// The page being shown. Until the first navigation commits, vue-router reports
// START_LOCATION, whose path is "/", for every URL: a PDP landing drew the home
// page's overlay header, then dropped it into the flow when the route committed,
// moving the whole page down by the header's height.
const shownPath = computed(() => (route.matched.length ? route.path : location.pathname))
const overlay = computed(() => {
  // Two flags on purpose: `transparent_home` is what three shipped themes
  // already store on the header section, and `layout.header_overlay_home` is the
  // copy the CSS generator can see (it zeroes the pre-JS shell's header
  // reservation, which is what keeps CLS at zero). Either one turns it on.
  const on = hdr.value.transparent_home === true || bootstrap.themeSettings.layout?.header_overlay_home === true
  return (
    (on && shownPath.value === "/") ||
    (hdr.value.sticky_collection === true && shownPath.value.startsWith("/collections"))
  )
})
const headerClass = computed(() => [
  searchIcon.value ? "dk-header--search-icon" : null,
  overlay.value ? "dk-header--overlay" : null,
  // Paint-only: the header keeps its overlay geometry and just stops being
  // transparent once there is page content behind it instead of the hero.
  overlay.value && scrolled.value ? "dk-header--solid" : null,
  hdr.value.sticky === "none" ? "dk-header--static" : null,
  hdr.value.sticky === "on-scroll-up" && !scrollUp.value ? "dk-header--hidden" : null,
])

// on-scroll-up: reveal when the shopper scrolls back up, hide going down.
const scrollUp = ref(true)
/** Past the very top — drives the overlay header's opaque state. Small threshold
 *  on purpose: being late here means white nav on white content, which is far
 *  worse than losing the transparent effect a few pixels early. */
const scrolled = ref(false)
let lastY = 0
function onScroll() {
  const y = window.scrollY
  scrollUp.value = y < 80 || y < lastY
  scrolled.value = y > 16
  lastY = y
}
onMounted(() => {
  // Unconditional: `overlay` is route-dependent (home only), so a listener
  // attached once for a single sticky mode would be missing on exactly the page
  // that needs it after a client-side navigation. It is passive and reads one
  // property, so the cost of always listening is not worth a conditional bug.
  window.addEventListener("scroll", onScroll, { passive: true })
  onScroll()
})
onBeforeUnmount(() => window.removeEventListener("scroll", onScroll))

const menu = computed(() => {
  // A menu handle on the section wins; otherwise the store's main menu, which
  // is what every shipped theme resolves to.
  const handle = String(hdr.value.menu ?? "")
  const named = handle ? bootstrap.payload?.menus?.[handle] : undefined
  const list = (named && named.length ? named : bootstrap.mainMenu) ?? []
  return list.length
    ? list
    : [
        { label: "Home", url: "/" },
        { label: "Shop all", url: "/collections/all" },
      ]
})

// ---- mega menu / dropdowns ------------------------------------------------
const openMenu = ref(-1)
// A top-level item opens a panel when it has children or a feature image.
function hasPanel(item: MenuItem) {
  return !!(item.children && item.children.length) || !!item.image
}
// A panel is a plain DROPDOWN (one vertical list) unless it has something that
// genuinely needs the wide grid: a child with grandchildren — which is what a
// column with a heading is — or a feature image beside the links.
//
// Without this every panel was the wide grid, and a `.dk-mega__col` is a COLUMN:
// four leaf links became four columns spread across a ~900px panel with 2.25rem
// gutters, i.e. a menu whose items ran horizontally away from the trigger that
// opened them. That is the shape a mega menu wants and a dropdown never does,
// and most store menus are one level deep, so the ugly case was the common one.
function isSimple(item: MenuItem) {
  if (item.image) return false
  return (item.children ?? []).every((c) => !c.children?.length)
}
function closeMenu() {
  openMenu.value = -1
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    closeMenu()
    closeResults()
    searchOpen.value = false
  }
}
if (typeof window !== "undefined") window.addEventListener("keydown", onKeydown)
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown))

// ---- predictive search ----------------------------------------------------
const results = ref<ProductCard[]>([])
const searching = ref(false)
const showResults = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
let seq = 0 // guards against out-of-order responses

watch(q, (val) => {
  const term = val.trim()
  if (timer) clearTimeout(timer)
  if (term.length < 2) {
    results.value = []
    showResults.value = false
    return
  }
  showResults.value = true
  searching.value = true
  const mine = ++seq
  timer = setTimeout(async () => {
    try {
      const res = await api.search(term)
      if (mine === seq) results.value = res.products.slice(0, 6)
    } catch {
      if (mine === seq) results.value = []
    } finally {
      if (mine === seq) searching.value = false
    }
  }, 250)
})

function closeResults() {
  showResults.value = false
}
function submitSearch() {
  const term = q.value.trim()
  if (!term) return
  closeResults()
  searchOpen.value = false
  router.push({ path: "/search", query: { q: term } })
}
// Close when focus leaves the whole search widget (blur to a non-child).
function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null
  if (!next || !(e.currentTarget as HTMLElement).contains(next)) {
    closeResults()
    searchOpen.value = false
  }
}

// Close search + mega menu on navigation.
watch(() => route.fullPath, () => {
  closeResults()
  closeMenu()
  searchOpen.value = false
})
onBeforeUnmount(() => timer && clearTimeout(timer))
</script>
